/**
 * Cancella i dati offline locali prima di applicare un aggiornamento PWA.
 * A differenza di clearOfflineUserData (usata al logout), questa funzione
 * NON tocca lo stato redux-persist (auth, carrello, bozze) in modo che
 * l’utente rimanga autenticato dopo il reload del service worker.
 *
 * Tutte le operazioni lunghe sono limitate da timeout: in caso di blocco
 * (IndexedDB, OPFS) si prosegue comunque verso il reload.
 */

import { dbBridge } from '../../orm/worker/db-bridge';
import { db } from '../../orm/connection';
import { sessionStateService } from './session-state.service';
import { debugWarn } from '../../utils/debug';

const PWA_SYNC_LS_KEYS = [
  'pwa_last_auto_sync_date',
  'pwa_last_route',
  'pwa_last_seen',
] as const;

const DB_CLOSE_TIMEOUT_MS = 4_000;
const FULL_RESET_TIMEOUT_MS = 12_000;
const SESSION_CLEAR_TIMEOUT_MS = 2_500;
const CRITICAL_IDB_DELETE_MAX_MS = 4_000;

async function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label: string
): Promise<T | 'timeout'> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<'timeout'>((resolve) => {
    timer = setTimeout(() => resolve('timeout'), ms);
  });
  try {
    const result = await Promise.race([promise, timeout]);
    if (timer) clearTimeout(timer);
    if (result === 'timeout') {
      debugWarn(`[clearOfflineDataForPWAUpdate] ${label} timed out after ${ms}ms`);
      return 'timeout';
    }
    return result as T;
  } catch (e) {
    if (timer) clearTimeout(timer);
    throw e;
  }
}

function clearPwaSyncLocalStorageKeys(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    for (const k of PWA_SYNC_LS_KEYS) {
      localStorage.removeItem(k);
    }
  } catch {
    /* ignore */
  }
}

/**
 * deleteDatabase può restare in “blocked” senza mai risolvere: hard cap assoluto.
 */
function deleteCriticalIndexedDbBounded(): Promise<void> {
  return new Promise((resolve) => {
    const finish = (): void => {
      resolve();
    };
    const hardStop = window.setTimeout(finish, CRITICAL_IDB_DELETE_MAX_MS);
    try {
      const req = indexedDB.deleteDatabase('b2b_offline_critical');
      req.onsuccess = () => {
        window.clearTimeout(hardStop);
        finish();
      };
      req.onerror = () => {
        window.clearTimeout(hardStop);
        finish();
      };
      req.onblocked = () => {
        window.clearTimeout(hardStop);
        finish();
      };
    } catch {
      window.clearTimeout(hardStop);
      finish();
    }
  });
}

export async function clearOfflineDataForPWAUpdate(): Promise<void> {
  const closeOutcome = await withTimeout(
    Promise.resolve().then(() => db.close()),
    DB_CLOSE_TIMEOUT_MS,
    'db.close'
  );
  if (closeOutcome === 'timeout') {
    dbBridge.forceKillWorker();
  }

  const resetPromise = (async (): Promise<void> => {
    try {
      const outcome = await withTimeout(
        dbBridge.fullReset('b2b_offline'),
        FULL_RESET_TIMEOUT_MS,
        'dbBridge.fullReset'
      );
      if (outcome === 'timeout') {
        dbBridge.forceKillWorker();
      }
    } catch (e) {
      debugWarn('[clearOfflineDataForPWAUpdate] SQLite fullReset:', e);
      dbBridge.forceKillWorker();
    }
  })();

  const sessionPromise = (async (): Promise<void> => {
    try {
      const outcome = await withTimeout(
        sessionStateService.clear(),
        SESSION_CLEAR_TIMEOUT_MS,
        'sessionState.clear'
      );
      if (outcome === 'timeout') {
        debugWarn('[clearOfflineDataForPWAUpdate] sessionState.clear gave up after timeout');
      }
    } catch (e) {
      debugWarn('[clearOfflineDataForPWAUpdate] sessionState:', e);
    }
  })();

  clearPwaSyncLocalStorageKeys();

  await Promise.all([resetPromise, sessionPromise]);

  try {
    await deleteCriticalIndexedDbBounded();
  } catch {
    /* ignore */
  }
}
